import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Eye,
  Save,
  X,
  Filter,
  Download,
  AlertCircle,
  Upload,
  CheckCircle,
  FileText,
} from "lucide-react";
import Layout from "../component/Layout";
import { db } from "../services/firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import "./ProductConstraints.css";

import gemsData from "./constant/gems.json";
import pearlData from "./constant/pearl.json";

const masterData = {
  GEMS: gemsData.GEMS || {},
  PEARL: pearlData.PEARL || {},
};

const PearlGemsConstraints = () => {
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);

  // Upload state
  const uploadInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const [productSpecs, setProductSpecs] = useState([]);

  const newProductTemplate = {
    segment: "",
    category: "",
    productSource: "",
    productName: "",
    finishType: "",
    color: "",
    shape: "",
    maxCaratRate: "",
    maxCaratRateOperator: "",
    maxCaratRateJmi: "",
    maxReadyServeSla: "",
    orderServeSla: "",
  };

  const [newProduct, setNewProduct] = useState(newProductTemplate);

  const [filters, setFilters] = useState({
    segment: "",
    category: "",
    productSource: "",
    productName: "",
    finishType: "",
    color: "",
    shape: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Extract master data helpers
  const segments = ["GEMS", "PEARL"];

  const getCategories = (seg) => {
    const s = (seg || "").toUpperCase();
    return s && masterData[s] ? Object.keys(masterData[s]) : [];
  };

  const getProductSources = (seg, cat) => {
    const s = (seg || "").toUpperCase();
    return s && cat && masterData[s]?.[cat]
      ? Object.keys(masterData[s][cat])
      : [];
  };

  const getProductNames = (seg, cat, src) => {
    const s = (seg || "").toUpperCase();
    return s && cat && src && masterData[s]?.[cat]?.[src]
      ? Object.keys(masterData[s][cat][src])
      : [];
  };

  const getProductData = (seg, cat, src, prodName) => {
    const s = (seg || "").toUpperCase();
    return s && cat && src && prodName
      ? masterData[s]?.[cat]?.[src]?.[prodName]
      : null;
  };

  const isRudrakshaProduct = (prodName) => {
    return (prodName || "").trim().toUpperCase() === "RUDRAKSHA";
  };

  const getFinishTypes = (seg, cat, src, prodName) => {
    const pData = getProductData(seg, cat, src, prodName);
    if (!pData) return [];

    if (Array.isArray(pData.finishTypes)) {
      return pData.finishTypes;
    }
    if (pData.finishTypes && typeof pData.finishTypes === "object") {
      return Object.keys(pData.finishTypes);
    }
    if ((seg || "").toUpperCase() === "GEMS") {
      return ["Transparent", "SemiTrnsprnt", "Translucent", "Opaque"];
    }
    if ((seg || "").toUpperCase() === "PEARL") {
      return ["Excellent", "Good", "Fair"];
    }
    return [];
  };

  const getColors = (seg, cat, src, prodName, finishType) => {
    if (isRudrakshaProduct(prodName)) return [];
    const pData = getProductData(seg, cat, src, prodName);
    const s = (seg || "").toUpperCase();
    const colors = new Set();

    if (pData && Array.isArray(pData.color)) {
      pData.color.forEach((c) => colors.add(c));
    }
    if (
      pData &&
      pData.finishTypes &&
      typeof pData.finishTypes === "object" &&
      finishType &&
      Array.isArray(pData.finishTypes[finishType])
    ) {
      pData.finishTypes[finishType].forEach((c) => colors.add(c));
    }
    ["HUE", "TINT", "TONE", "SHADE"].forEach((c) => colors.add(c));
    if (s === "PEARL") {
      ["White", "Creamish-White", "Pinkish-White"].forEach((c) =>
        colors.add(c),
      );
    }
    return Array.from(colors);
  };

  const getShapes = (seg, cat, src, prodName) => {
    if (isRudrakshaProduct(prodName)) return [];
    const pData = getProductData(seg, cat, src, prodName);
    const s = (seg || "").toUpperCase();
    const shapes = new Set();

    if (pData && Array.isArray(pData.shape)) {
      pData.shape.forEach((shp) => shapes.add(shp));
    }
    if (s === "GEMS") {
      [
        "Round",
        "Square",
        "Oval",
        "Heart",
        "Pear",
        "Cushion",
        "Cabochon",
      ].forEach((shp) => shapes.add(shp));
    }
    if (s === "PEARL") {
      [
        "Round",
        "Near-Round",
        "Baroque",
        "Button",
        "Drop",
        "Square",
        "Oval",
      ].forEach((shp) => shapes.add(shp));
    }
    return Array.from(shapes);
  };

  // Check if combination is valid in master data
  const isValidCombination = (product) => {
    const seg = (product.segment || "").trim().toUpperCase();
    const cat = (product.category || "").trim();
    const src = (product.productSource || "").trim();
    const prodName = (product.productName || "").trim();
    const finish = (product.finishType || "").trim();
    const color = (product.color || "").trim();
    const shape = (product.shape || "").trim();

    if (!seg || !cat || !src || !prodName || !finish) return false;

    const root = masterData[seg];
    if (!root || !root[cat] || !root[cat][src] || !root[cat][src][prodName])
      return false;

    const allowedFinishes = getFinishTypes(seg, cat, src, prodName);
    const finishMatches = allowedFinishes.some(
      (f) => f.toUpperCase() === finish.toUpperCase(),
    );
    if (!finishMatches) return false;

    if (isRudrakshaProduct(prodName)) {
      return true;
    }

    if (!color || !shape) return false;

    const allowedColors = getColors(seg, cat, src, prodName, finish);
    const colorMatches = allowedColors.some(
      (c) => c.toUpperCase() === color.toUpperCase(),
    );
    if (!colorMatches && allowedColors.length > 0) return false;

    const allowedShapes = getShapes(seg, cat, src, prodName);
    const shapeMatches = allowedShapes.some(
      (s) => s.toUpperCase() === shape.toUpperCase(),
    );
    if (!shapeMatches && allowedShapes.length > 0) return false;

    return true;
  };

  // Duplicate check
  const isDuplicateEntry = (product, list = productSpecs) => {
    const toS = (v) =>
      String(v ?? "")
        .trim()
        .toUpperCase();
    const isRudra = isRudrakshaProduct(product.productName);

    return list.some((spec) => {
      if (spec.id && product.id && spec.id === product.id) return false;

      const sameSeg = toS(spec.segment) === toS(product.segment);
      const sameCat = toS(spec.category) === toS(product.category);
      const sameSrc = toS(spec.productSource) === toS(product.productSource);
      const sameName = toS(spec.productName) === toS(product.productName);
      const sameFinish = toS(spec.finishType) === toS(product.finishType);

      if (!sameSeg || !sameCat || !sameSrc || !sameName || !sameFinish) {
        return false;
      }

      if (isRudra) {
        return true;
      }

      const sameColor = toS(spec.color) === toS(product.color);
      const sameShape = toS(spec.shape) === toS(product.shape);
      return sameColor && sameShape;
    });
  };

  // Fetch from Firestore (Pearl and Gems only)
  useEffect(() => {
    const fetchProductSpecs = async () => {
      try {
        const q = query(
          collection(db, "productSpecs"),
          where("segment", "in", [
            "PEARL",
            "GEMS",
            "pearl",
            "gems",
            "Pearl",
            "Gems",
          ]),
        );
        const querySnapshot = await getDocs(q);
        const specsData = [];
        const toStr = (v) => (v === null || v === undefined ? "" : String(v));

        querySnapshot.forEach((docSnap) => {
          const d = docSnap.data();
          const seg = toStr(d.segment).trim().toUpperCase();
          if (seg === "PEARL" || seg === "GEMS") {
            specsData.push({
              id: docSnap.id,
              ...d,
              segment: seg,
              category: toStr(d.category),
              productSource: toStr(d.productSource),
              productName: toStr(d.productName),
              finishType: toStr(d.finishType),
              color: toStr(d.color),
              shape: toStr(d.shape),
              maxCaratRate: d.maxCaratRate ?? "",
              maxCaratRateOperator: d.maxCaratRateOperator ?? "",
              maxCaratRateJmi: d.maxCaratRateJmi ?? "",
              maxReadyServeSla: d.maxReadyServeSla ?? "",
              orderServeSla: d.orderServeSla ?? "",
            });
          }
        });

        setProductSpecs(specsData);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching pearl & gems specs:", err);
        setLoading(false);
      }
    };

    fetchProductSpecs();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    if (name === "segment") {
      setFilters({
        segment: value,
        category: "",
        productSource: "",
        productName: "",
        finishType: "",
        color: "",
        shape: "",
      });
    } else if (name === "category") {
      setFilters((prev) => ({
        ...prev,
        category: value,
        productSource: "",
        productName: "",
        finishType: "",
        color: "",
        shape: "",
      }));
    } else if (name === "productSource") {
      setFilters((prev) => ({
        ...prev,
        productSource: value,
        productName: "",
        finishType: "",
        color: "",
        shape: "",
      }));
    } else if (name === "productName") {
      setFilters((prev) => ({
        ...prev,
        productName: value,
        finishType: "",
        color: "",
        shape: "",
      }));
    } else if (name === "finishType") {
      setFilters((prev) => ({
        ...prev,
        finishType: value,
        color: "",
        shape: "",
      }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      segment: "",
      category: "",
      productSource: "",
      productName: "",
      finishType: "",
      color: "",
      shape: "",
    });
    setCurrentPage(1);
  };

  // Filter specs (GEMS and PEARL only)
  const filteredSpecs = useMemo(() => {
    return productSpecs.filter((product) => {
      const toS = (v) =>
        String(v ?? "")
          .trim()
          .toUpperCase();
      const seg = toS(product.segment);
      if (seg !== "GEMS" && seg !== "PEARL") return false;

      const segmentMatch = !filters.segment || seg === toS(filters.segment);
      const categoryMatch =
        !filters.category || toS(product.category) === toS(filters.category);
      const productSourceMatch =
        !filters.productSource ||
        toS(product.productSource) === toS(filters.productSource);
      const productNameMatch =
        !filters.productName ||
        toS(product.productName) === toS(filters.productName);
      const finishTypeMatch =
        !filters.finishType ||
        toS(product.finishType) === toS(filters.finishType);
      const colorMatch =
        !filters.color || toS(product.color) === toS(filters.color);
      const shapeMatch =
        !filters.shape || toS(product.shape) === toS(filters.shape);

      return (
        segmentMatch &&
        categoryMatch &&
        productSourceMatch &&
        productNameMatch &&
        finishTypeMatch &&
        colorMatch &&
        shapeMatch
      );
    });
  }, [productSpecs, filters]);

  // Paginate
  const paginatedSpecs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredSpecs.slice(startIndex, endIndex);
  }, [filteredSpecs, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filteredSpecs.length / itemsPerPage));
  }, [filteredSpecs.length, itemsPerPage]);

  // KPI calculations (GEMS and PEARL only)
  const kpiData = useMemo(() => {
    const validSpecs = productSpecs.filter((p) => {
      const s = String(p.segment || "")
        .trim()
        .toUpperCase();
      return s === "GEMS" || s === "PEARL";
    });
    const covered = validSpecs.length;
    const gemsCount = validSpecs.filter(
      (p) => (p.segment || "").toUpperCase() === "GEMS",
    ).length;
    const pearlCount = validSpecs.filter(
      (p) => (p.segment || "").toUpperCase() === "PEARL",
    ).length;
    const parseNum = (v) => parseFloat(v) || 0;

    let totalMaxRate = 0,
      totalSellerMax = 0,
      totalSLA = 0,
      slaCount = 0;
    validSpecs.forEach((p) => {
      const maxSeller = parseNum(p.maxCaratRate);
      const opPct = parseNum(p.maxCaratRateOperator);
      const jmiPct = parseNum(p.maxCaratRateJmi);
      const rowTotal =
        maxSeller + (maxSeller * opPct) / 100 + (maxSeller * jmiPct) / 100;

      totalSellerMax += maxSeller;
      totalMaxRate += rowTotal;
      const sla = parseNum(p.orderServeSla);
      if (sla > 0) {
        totalSLA += sla;
        slaCount++;
      }
    });

    return {
      covered,
      gemsCount,
      pearlCount,
      avgSellerMax: covered > 0 ? (totalSellerMax / covered).toFixed(1) : "0.0",
      avgMaxRate: covered > 0 ? (totalMaxRate / covered).toFixed(1) : "0.0",
      avgSLA: slaCount > 0 ? (totalSLA / slaCount).toFixed(1) : "—",
    };
  }, [productSpecs]);

  // Numeric helpers
  const fmtVal = (v) => {
    if (v === "" || v === null || v === undefined) return "—";
    const n = parseFloat(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString("en-IN", { maximumFractionDigits: 1 });
  };

  const safeNum = (v) => {
    const n = parseFloat(v);
    return isFinite(n) ? n : 0;
  };

  const calculateTotals = (product) => {
    const maxSeller = safeNum(product.maxCaratRate);
    const opPct = safeNum(product.maxCaratRateOperator);
    const jmiPct = safeNum(product.maxCaratRateJmi);

    const opAmount = (maxSeller * opPct) / 100;
    const jmiAmount = (maxSeller * jmiPct) / 100;
    const totalMax = maxSeller + opAmount + jmiAmount;

    return {
      maxSeller: fmtVal(product.maxCaratRate),
      maxOperator: opPct > 0 ? `${opPct}%` : "0%",
      maxOperatorAmount:
        opAmount > 0
          ? opAmount.toLocaleString("en-IN", { maximumFractionDigits: 1 })
          : "0",
      maxJmi: jmiPct > 0 ? `${jmiPct}%` : "0%",
      maxJmiAmount:
        jmiAmount > 0
          ? jmiAmount.toLocaleString("en-IN", { maximumFractionDigits: 1 })
          : "0",
      totalMax:
        totalMax > 0
          ? totalMax.toLocaleString("en-IN", { maximumFractionDigits: 1 })
          : "—",
    };
  };

  // Add Product handler
  const handleAddProduct = async (e) => {
    e.preventDefault();

    const numField = (v) =>
      v === "" || v === null || v === undefined ? "" : Number(v);

    const normalized = {
      ...newProduct,
      segment: (newProduct.segment || "").trim().toUpperCase(),
      category: (newProduct.category || "").trim(),
      productSource: (newProduct.productSource || "").trim(),
      productName: (newProduct.productName || "").trim(),
      finishType: (newProduct.finishType || "").trim(),
      color: isRudrakshaProduct(newProduct.productName)
        ? ""
        : (newProduct.color || "").trim(),
      shape: isRudrakshaProduct(newProduct.productName)
        ? ""
        : (newProduct.shape || "").trim(),
      maxCaratRate: numField(newProduct.maxCaratRate),
      maxCaratRateOperator: numField(newProduct.maxCaratRateOperator),
      maxCaratRateJmi: numField(newProduct.maxCaratRateJmi),
      maxReadyServeSla: numField(newProduct.maxReadyServeSla),
      orderServeSla: numField(newProduct.orderServeSla),
    };

    if (normalized.segment !== "GEMS" && normalized.segment !== "PEARL") {
      setError("Only GEMS and PEARL segments are allowed.");
      return;
    }

    if (!isValidCombination(normalized)) {
      setError("Selected product combination is not allowed in master data.");
      return;
    }

    if (isDuplicateEntry(normalized)) {
      setError("A product with these specifications already exists!");
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "productSpecs"), normalized);
      setProductSpecs((prev) => [...prev, { ...normalized, id: docRef.id }]);
      setShowAddProduct(false);
      setNewProduct(newProductTemplate);
      setError("");
    } catch (err) {
      console.error("Error adding product:", err);
      setError("Failed to add product. Please try again.");
    }
  };

  // Handle Edit Product
  const handleEdit = (product) => {
    setEditingProduct({
      ...product,
      segment: product.segment || "",
      category: product.category || "",
      productSource: product.productSource || "",
      productName: product.productName || "",
      finishType: product.finishType || "",
      color: product.color || "",
      shape: product.shape || "",
      maxCaratRate: product.maxCaratRate ?? "",
      maxCaratRateOperator: product.maxCaratRateOperator ?? "",
      maxCaratRateJmi: product.maxCaratRateJmi ?? "",
      maxReadyServeSla: product.maxReadyServeSla ?? "",
      orderServeSla: product.orderServeSla ?? "",
    });
    setError("");
  };

  // Save Edit handler
  const handleSave = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const numField = (v) =>
        v === "" || v === null || v === undefined ? "" : Number(v);

      const normalized = {
        ...editingProduct,
        segment: (editingProduct.segment || "").trim().toUpperCase(),
        category: (editingProduct.category || "").trim(),
        productSource: (editingProduct.productSource || "").trim(),
        productName: (editingProduct.productName || "").trim(),
        finishType: (editingProduct.finishType || "").trim(),
        color: isRudrakshaProduct(editingProduct.productName)
          ? ""
          : (editingProduct.color || "").trim(),
        shape: isRudrakshaProduct(editingProduct.productName)
          ? ""
          : (editingProduct.shape || "").trim(),
        maxCaratRate: numField(editingProduct.maxCaratRate),
        maxCaratRateOperator: numField(editingProduct.maxCaratRateOperator),
        maxCaratRateJmi: numField(editingProduct.maxCaratRateJmi),
        maxReadyServeSla: numField(editingProduct.maxReadyServeSla),
        orderServeSla: numField(editingProduct.orderServeSla),
      };

      if (normalized.segment !== "GEMS" && normalized.segment !== "PEARL") {
        setError("Only GEMS and PEARL segments are allowed.");
        setAdding(false);
        return;
      }

      if (!isValidCombination(normalized)) {
        setError("Selected product combination is not allowed in master data.");
        setAdding(false);
        return;
      }

      if (isDuplicateEntry(normalized)) {
        setError("Another product with these specifications already exists!");
        setAdding(false);
        return;
      }

      const docRef = doc(db, "productSpecs", editingProduct.id);
      await updateDoc(docRef, normalized);
      setProductSpecs((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? normalized : p)),
      );
      setEditingProduct(null);
      setError("");
    } catch (err) {
      console.error("Error updating product:", err);
      setError("Failed to update product. Please try again.");
    }
    setAdding(false);
  };

  // Export to CSV
  const downloadExcel = () => {
    let csvContent =
      "Segment,Category,Product Source,Product Name,Finish Type,Color,Shape,";
    csvContent +=
      "Max Carat Rate (Seller),Operator Carat Rate (%),JMI Carat Rate (%),Total Max Carat Rate,";
    csvContent += "Ready Serve SLA,Order Serve SLA\n";

    filteredSpecs.forEach((product) => {
      const totals = calculateTotals(product);
      csvContent += `${product.segment || ""},${product.category || ""},${product.productSource || ""},`;
      csvContent += `${product.productName || ""},${product.finishType || ""},${product.color || ""},${product.shape || ""},`;
      csvContent += `${totals.maxSeller},${totals.maxOperator},${totals.maxJmi},${totals.totalMax},`;
      csvContent += `${product.maxReadyServeSla || ""},${product.orderServeSla || ""}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "pearl_gems_constraints.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download blank template with sample rows
  const downloadTemplate = async () => {
    const XLSX = await import("xlsx");
    const headers = [
      "segment",
      "category",
      "productSource",
      "productName",
      "finishType",
      "color",
      "shape",
      "maxCaratRate",
      "maxCaratRateOperator",
      "maxCaratRateJmi",
      "maxReadyServeSla",
      "orderServeSla",
    ];

    const sampleRows = [
      headers,
      [
        "GEMS",
        "A",
        "JEWELLERY",
        "RUDRAKSHA",
        "1MukhiMoon",
        "",
        "",
        1200,
        5,
        5,
        2,
        7,
      ],
      [
        "GEMS",
        "A",
        "JEWELLERY",
        "RUBY",
        "Transparent",
        "HUE",
        "Round",
        2500,
        5,
        5,
        3,
        10,
      ],
      [
        "PEARL",
        "A",
        "BASARA",
        "LOOSE",
        "Excellent",
        "White",
        "Round",
        900,
        5,
        5,
        2,
        5,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(sampleRows);
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length + 4, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "PearlGemsConstraints");
    XLSX.writeFile(wb, "pearl_gems_constraints_template.xlsx");
  };

  // Bulk Excel upload handler
  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const XLSX = await import("xlsx");
    setUploading(true);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });

      if (!rows.length) {
        setUploadReport({
          added: 0,
          duplicates: 0,
          invalid: [{ row: "—", reason: "File is empty or has no data rows." }],
          errors: [],
        });
        setShowUploadModal(true);
        setUploading(false);
        return;
      }

      const existingKeys = new Set(
        productSpecs
          .filter((p) => {
            const s = (p.segment || "").trim().toUpperCase();
            return s === "GEMS" || s === "PEARL";
          })
          .map((p) => {
            const isR = isRudrakshaProduct(p.productName);
            const col = isR ? "" : p.color || "";
            const shp = isR ? "" : p.shape || "";
            return `${p.segment}||${p.category}||${p.productSource}||${p.productName}||${p.finishType}||${col}||${shp}`.toUpperCase();
          }),
      );
      const batchKeys = new Set();

      const report = { added: 0, duplicates: 0, invalid: [], errors: [] };
      const newDocs = [];

      rows.forEach((raw, idx) => {
        const rowNum = idx + 2;

        const val = (k, aliases = []) => {
          if (raw[k] !== undefined && raw[k] !== "") return raw[k];
          for (const a of aliases) {
            if (raw[a] !== undefined && raw[a] !== "") return raw[a];
          }
          return "";
        };

        const segment = String(val("segment", ["Segment", "SEGMENT"]))
          .trim()
          .toUpperCase();
        const category = String(
          val("category", ["Category", "CATEGORY"]),
        ).trim();
        const productSource = String(
          val("productSource", [
            "Product Source",
            "product_source",
            "PRODUCT_SOURCE",
          ]),
        ).trim();
        const productName = String(
          val("productName", ["Product Name", "product_name", "PRODUCT_NAME"]),
        ).trim();
        const finishType = String(
          val("finishType", ["Finish Type", "finish_type", "FINISH_TYPE"]),
        ).trim();
        const color = String(val("color", ["Color", "COLOR"])).trim();
        const shape = String(val("shape", ["Shape", "SHAPE"])).trim();

        if (
          !segment ||
          !category ||
          !productSource ||
          !productName ||
          !finishType
        ) {
          report.invalid.push({
            row: rowNum,
            reason: `Missing identity fields (segment, category, productSource, productName, finishType required).`,
          });
          return;
        }

        if (segment !== "GEMS" && segment !== "PEARL") {
          report.invalid.push({
            row: rowNum,
            reason: `Segment '${segment}' is not allowed. Pearl & Gems Constraints only supports GEMS and PEARL.`,
          });
          return;
        }

        const isRudra = isRudrakshaProduct(productName);
        if (!isRudra && (!color || !shape)) {
          report.invalid.push({
            row: rowNum,
            reason: `Product '${productName}' requires color and shape specifications.`,
          });
          return;
        }

        const candidate = {
          segment,
          category,
          productSource,
          productName,
          finishType,
          color: isRudra ? "" : color,
          shape: isRudra ? "" : shape,
        };

        if (!isValidCombination(candidate)) {
          report.invalid.push({
            row: rowNum,
            reason: `Combination not found in master data (${segment} › ${category} › ${productSource} › ${productName} › ${finishType} ${!isRudra ? `› ${color} › ${shape}` : ""})`,
          });
          return;
        }

        const comboKey =
          `${segment}||${category}||${productSource}||${productName}||${finishType}||${candidate.color}||${candidate.shape}`.toUpperCase();
        if (existingKeys.has(comboKey) || batchKeys.has(comboKey)) {
          report.duplicates++;
          return;
        }
        batchKeys.add(comboKey);

        const numField = (v) => parseFloat(v) || 0;
        newDocs.push({
          ...candidate,
          maxCaratRate: numField(
            val("maxCaratRate", [
              "maxCaratRateSeller",
              "Max Carat Rate",
              "Max Carat Rate Seller",
            ]),
          ),
          maxCaratRateOperator: numField(
            val("maxCaratRateOperator", [
              "caratRateOperator",
              "maxoperator",
              "Max Operator Carat Rate",
            ]),
          ),
          maxCaratRateJmi: numField(
            val("maxCaratRateJmi", [
              "caratRateJmi",
              "maxjmi",
              "Max JMI Carat Rate",
            ]),
          ),
          maxReadyServeSla: numField(
            val("maxReadyServeSla", [
              "Ready Serve SLA",
              "maxReadyServeSlaDays",
            ]),
          ),
          orderServeSla: numField(
            val("orderServeSla", ["Order Serve SLA", "orderServeSlaDays"]),
          ),
        });
      });

      const addedDocs = [];
      for (const data of newDocs) {
        try {
          const ref = await addDoc(collection(db, "productSpecs"), data);
          addedDocs.push({ ...data, id: ref.id });
          report.added++;
        } catch (err) {
          report.errors.push(err.message);
        }
      }

      if (addedDocs.length) {
        setProductSpecs((prev) => [...prev, ...addedDocs]);
      }

      setUploadReport(report);
      setShowUploadModal(true);
    } catch (err) {
      setUploadReport({
        added: 0,
        duplicates: 0,
        invalid: [],
        errors: [`Failed to parse file: ${err.message}`],
      });
      setShowUploadModal(true);
    }
    setUploading(false);
  };

  // Render Add / Edit Product Form
  const renderProductForm = (
    product,
    setProduct,
    onSubmit,
    isEditing = false,
  ) => {
    const isRudra = isRudrakshaProduct(product.productName);
    const availableCategories = getCategories(product.segment);
    const availableSources = getProductSources(
      product.segment,
      product.category,
    );
    const availableProducts = getProductNames(
      product.segment,
      product.category,
      product.productSource,
    );
    const availableFinishes = getFinishTypes(
      product.segment,
      product.category,
      product.productSource,
      product.productName,
    );
    const availableColors = getColors(
      product.segment,
      product.category,
      product.productSource,
      product.productName,
      product.finishType,
    );
    const availableShapes = getShapes(
      product.segment,
      product.category,
      product.productSource,
      product.productName,
    );

    const maxSeller = safeNum(product.maxCaratRate);
    const opPct = safeNum(product.maxCaratRateOperator);
    const jmiPct = safeNum(product.maxCaratRateJmi);
    const opAmount = (maxSeller * opPct) / 100;
    const jmiAmount = (maxSeller * jmiPct) / 100;
    const totalMaxRate = maxSeller + opAmount + jmiAmount;

    return (
      <form onSubmit={onSubmit} className="product-form">
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Segment Details */}
        <div className="form-section">
          <h4>Segment & Material Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Segment</label>
              <select
                value={product.segment}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    segment: e.target.value,
                    category: "",
                    productSource: "",
                    productName: "",
                    finishType: "",
                    color: "",
                    shape: "",
                  }))
                }
                required
              >
                <option value="">Select Segment</option>
                {segments.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Category (Purity / Grade)</label>
              <select
                value={product.category}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    category: e.target.value,
                    productSource: "",
                    productName: "",
                    finishType: "",
                    color: "",
                    shape: "",
                  }))
                }
                required
                disabled={!product.segment}
              >
                <option value="">Select Category</option>
                {availableCategories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="form-section">
          <h4>Product Specification</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Product Source</label>
              <select
                value={product.productSource}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    productSource: e.target.value,
                    productName: "",
                    finishType: "",
                    color: "",
                    shape: "",
                  }))
                }
                required
                disabled={!product.category}
              >
                <option value="">Select Source</option>
                {availableSources.map((src) => (
                  <option key={src} value={src}>
                    {src}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Product Name</label>
              <select
                value={product.productName}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    productName: e.target.value,
                    finishType: "",
                    color: "",
                    shape: "",
                  }))
                }
                required
                disabled={!product.productSource}
              >
                <option value="">Select Product Name</option>
                {availableProducts.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Finish Type</label>
              <select
                value={product.finishType}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    finishType: e.target.value,
                    color: "",
                  }))
                }
                required
                disabled={!product.productName}
              >
                <option value="">Select Finish Type</option>
                {availableFinishes.map((finish) => (
                  <option key={finish} value={finish}>
                    {finish}
                  </option>
                ))}
              </select>
            </div>

            {/* Color field: Hidden/Disabled for RUDRAKSHA */}
            <div className="form-field">
              <label>Color</label>
              {isRudra ? (
                <input
                  type="text"
                  value="N/A (Not Applicable for Rudraksha)"
                  disabled
                  style={{
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                />
              ) : (
                <select
                  value={product.color}
                  onChange={(e) =>
                    setProduct((prev) => ({ ...prev, color: e.target.value }))
                  }
                  required={!isRudra}
                  disabled={!product.finishType}
                >
                  <option value="">Select Color</option>
                  {availableColors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Shape field: Hidden/Disabled for RUDRAKSHA */}
            <div className="form-field">
              <label>Shape</label>
              {isRudra ? (
                <input
                  type="text"
                  value="N/A (Not Applicable for Rudraksha)"
                  disabled
                  style={{
                    backgroundColor: "#f3f4f6",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                />
              ) : (
                <select
                  value={product.shape}
                  onChange={(e) =>
                    setProduct((prev) => ({ ...prev, shape: e.target.value }))
                  }
                  required={!isRudra}
                  disabled={!product.productName}
                >
                  <option value="">Select Shape</option>
                  {availableShapes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>

        {/* Carat Rate Constraints Section */}
        <div className="form-section">
          <h4>Carat Rate Constraints</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Carat Rate (Seller) (₹)</label>
              <input
                type="number"
                step="0.01"
                placeholder="e.g. 1500"
                value={product.maxCaratRate}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    maxCaratRate: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="form-field">
              <label>Operator Carat Rate in percentage (%)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g. 5 (5% of Max Carat Rate)"
                value={product.maxCaratRateOperator}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    maxCaratRateOperator: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="form-field">
              <label>JMI Carat Rate in percentage (%)</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g. 5 (5% of Max Carat Rate)"
                value={product.maxCaratRateJmi}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    maxCaratRateJmi: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>

          <div
            style={{
              marginTop: "0.75rem",
              fontSize: "0.85rem",
              color: "#8a6b32",
              fontWeight: 600,
              display: "flex",
              flexWrap: "wrap",
              gap: "1.2rem",
              padding: "0.5rem 0.75rem",
              backgroundColor: "#fef9c3",
              borderRadius: "6px",
              border: "1px solid #fde047",
            }}
          >
            <span>
              Seller Max: ₹
              {maxSeller > 0
                ? maxSeller.toLocaleString("en-IN", {
                    maximumFractionDigits: 1,
                  })
                : "0"}
            </span>
            <span>
              + Operator ({opPct}%): ₹
              {opAmount > 0
                ? opAmount.toLocaleString("en-IN", { maximumFractionDigits: 1 })
                : "0"}
            </span>
            <span>
              + JMI ({jmiPct}%): ₹
              {jmiAmount > 0
                ? jmiAmount.toLocaleString("en-IN", {
                    maximumFractionDigits: 1,
                  })
                : "0"}
            </span>
            <span style={{ fontWeight: 700, color: "#15803d" }}>
              = Total Max: ₹
              {totalMaxRate > 0
                ? totalMaxRate.toLocaleString("en-IN", {
                    maximumFractionDigits: 1,
                  })
                : "0.0"}{" "}
              / Carat
            </span>
          </div>
        </div>

        {/* SLA Details */}
        <div className="form-section">
          <h4>SLA Details</h4>
          <div className="form-grid">
            <div className="form-field">
              <label>Max Ready Serve SLA (Days)</label>
              <input
                type="number"
                placeholder="e.g. 2"
                value={product.maxReadyServeSla}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    maxReadyServeSla: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="form-field">
              <label>Order Serve SLA (Days)</label>
              <input
                type="number"
                placeholder="e.g. 7"
                value={product.orderServeSla}
                onChange={(e) =>
                  setProduct((prev) => ({
                    ...prev,
                    orderServeSla: e.target.value,
                  }))
                }
                required
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => {
              if (onSubmit === handleAddProduct) {
                setShowAddProduct(false);
                setNewProduct(newProductTemplate);
              }
              if (onSubmit === handleSave) {
                setEditingProduct(null);
              }
              setError("");
            }}
            className="btn-secondary"
          >
            Cancel
          </button>
          {!adding && (
            <button type="submit" className="btn-primary">
              {isEditing ? "Save Changes" : "Add Product"}
            </button>
          )}
        </div>
      </form>
    );
  };

  if (loading) {
    return (
      <Layout pageTitle="Pearl & Gems Constraints">
        <div className="loading">Loading Pearl & Gems Constraints...</div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Pearl & Gems Constraints">
      <div className="product-constraints-container">
        <h1>Pearl & Gems Constraints</h1>
        <p>
          Manage constraint rates, SLA, colors, and shapes for Gems (including
          Rudraksha) and Pearl products.
        </p>

        {/* KPI Cards */}
        <div className="pc-kpi-grid">
          <div className="pc-kpi-card pc-kpi-total">
            <div className="pc-kpi-icon">💎</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading ? "—" : kpiData.covered.toLocaleString()}
              </div>
              <div className="pc-kpi-label">Configured</div>
              <div className="pc-kpi-hint">Total gem & pearl specs</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-segments">
            <div className="pc-kpi-icon">✨</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading ? "—" : kpiData.gemsCount.toLocaleString()}
              </div>
              <div className="pc-kpi-label">Gems Configured</div>
              <div className="pc-kpi-hint">Including Rudraksha</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-sources">
            <div className="pc-kpi-icon">⚪</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading ? "—" : kpiData.pearlCount.toLocaleString()}
              </div>
              <div className="pc-kpi-label">Pearl Configured</div>
              <div className="pc-kpi-hint">Basara, Akoya, etc.</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-wastage">
            <div className="pc-kpi-icon">💰</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading ? "—" : `₹${kpiData.avgSellerMax}`}
              </div>
              <div className="pc-kpi-label">Avg Max Rate (Seller)</div>
              <div className="pc-kpi-hint">Base max carat rate</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-mc">
            <div className="pc-kpi-icon">📈</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading ? "—" : `₹${kpiData.avgMaxRate}`}
              </div>
              <div className="pc-kpi-label">Avg Total Max Rate</div>
              <div className="pc-kpi-hint">Seller + Op + JMI</div>
            </div>
          </div>

          <div className="pc-kpi-card pc-kpi-sla">
            <div className="pc-kpi-icon">⏱️</div>
            <div className="pc-kpi-body">
              <div className="pc-kpi-value">
                {loading
                  ? "—"
                  : kpiData.avgSLA === "—"
                    ? "—"
                    : `${kpiData.avgSLA}d`}
              </div>
              <div className="pc-kpi-label">Avg Order SLA</div>
              <div className="pc-kpi-hint">Average serve days</div>
            </div>
          </div>
        </div>

        <div className="content-wrapper">
          {/* Hidden file input for Excel upload */}
          <input
            ref={uploadInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={handleExcelUpload}
          />

          <div className="header-section">
            <h2>Specifications & Constraints</h2>
            <div className="header-actions">
              <button
                onClick={downloadTemplate}
                className="btn-secondary download-btn"
                title="Download template with sample data"
              >
                <FileText size={16} /> Template
              </button>
              <button
                onClick={downloadExcel}
                className="btn-secondary download-btn"
                title="Export to CSV"
              >
                <Download size={16} /> Export
              </button>
              <button
                onClick={() => uploadInputRef.current?.click()}
                disabled={uploading}
                className="btn-upload"
              >
                <Upload size={16} />
                {uploading ? "Uploading…" : "Upload Excel"}
              </button>
              <button
                onClick={() => {
                  setShowAddProduct(true);
                  setError("");
                }}
                className="btn-primary add-product-btn"
              >
                <Plus /> Add Product
              </button>
            </div>
          </div>

          {/* Upload Report Modal */}
          {showUploadModal && uploadReport && (
            <div
              className="modal-overlay"
              onClick={() => setShowUploadModal(false)}
            >
              <div
                className="modal-content upload-report-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>📥 Upload Report</h3>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="modal-close"
                  >
                    <X />
                  </button>
                </div>

                <div className="upload-report-summary">
                  <div className="ur-badge ur-badge-added">
                    <CheckCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.added}</div>
                      <div className="ur-lbl">Added</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-dup">
                    <AlertCircle size={20} />
                    <div>
                      <div className="ur-num">{uploadReport.duplicates}</div>
                      <div className="ur-lbl">Duplicates Skipped</div>
                    </div>
                  </div>
                  <div className="ur-badge ur-badge-inv">
                    <X size={20} />
                    <div>
                      <div className="ur-num">
                        {uploadReport.invalid.length}
                      </div>
                      <div className="ur-lbl">Invalid Rows</div>
                    </div>
                  </div>
                  {uploadReport.errors.length > 0 && (
                    <div className="ur-badge ur-badge-err">
                      <AlertCircle size={20} />
                      <div>
                        <div className="ur-num">
                          {uploadReport.errors.length}
                        </div>
                        <div className="ur-lbl">Write Errors</div>
                      </div>
                    </div>
                  )}
                </div>

                {uploadReport.invalid.length > 0 && (
                  <div className="upload-report-section">
                    <h4>⚠️ Invalid Rows</h4>
                    <div className="upload-report-table-wrap">
                      <table className="upload-report-table">
                        <thead>
                          <tr>
                            <th>Row #</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadReport.invalid.map((item, i) => (
                            <tr key={i}>
                              <td
                                style={{
                                  whiteSpace: "nowrap",
                                  fontWeight: 600,
                                }}
                              >
                                {item.row}
                              </td>
                              <td
                                style={{ fontSize: "0.8rem", color: "#dc2626" }}
                              >
                                {item.reason}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {uploadReport.errors.length > 0 && (
                  <div className="upload-report-section">
                    <h4>🔥 Firestore Errors</h4>
                    <ul
                      style={{
                        fontSize: "0.82rem",
                        color: "#dc2626",
                        paddingLeft: "1rem",
                      }}
                    >
                      {uploadReport.errors.map((e, i) => (
                        <li key={i}>{e}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {uploadReport.added > 0 &&
                  uploadReport.invalid.length === 0 &&
                  uploadReport.errors.length === 0 && (
                    <p
                      style={{
                        color: "#059669",
                        fontWeight: 600,
                        textAlign: "center",
                        marginTop: "1rem",
                      }}
                    >
                      ✅ All rows imported successfully!
                    </p>
                  )}

                <div className="form-actions">
                  <button
                    className="btn-primary"
                    onClick={() => setShowUploadModal(false)}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Product Modal */}
          {showAddProduct && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <h3>Add Pearl & Gems Specification</h3>
                  <button
                    onClick={() => {
                      setShowAddProduct(false);
                      setError("");
                    }}
                    className="modal-close"
                  >
                    <X />
                  </button>
                </div>
                {renderProductForm(
                  newProduct,
                  setNewProduct,
                  handleAddProduct,
                  false,
                )}
              </div>
            </div>
          )}

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filters-header">
              <Filter />
              <h3>Filters</h3>
            </div>
            <div className="filters-grid">
              <div className="filter-field">
                <label>Segment</label>
                <select
                  name="segment"
                  value={filters.segment}
                  onChange={handleFilterChange}
                >
                  <option value="">All (Gems & Pearl)</option>
                  {segments.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Category</label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  disabled={!filters.segment}
                >
                  <option value="">All Categories</option>
                  {getCategories(filters.segment).map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Product Source</label>
                <select
                  name="productSource"
                  value={filters.productSource}
                  onChange={handleFilterChange}
                  disabled={!filters.category}
                >
                  <option value="">All Sources</option>
                  {getProductSources(filters.segment, filters.category).map(
                    (src) => (
                      <option key={src} value={src}>
                        {src}
                      </option>
                    ),
                  )}
                </select>
              </div>

              <div className="filter-field">
                <label>Product Name</label>
                <select
                  name="productName"
                  value={filters.productName}
                  onChange={handleFilterChange}
                  disabled={!filters.productSource}
                >
                  <option value="">All Products</option>
                  {getProductNames(
                    filters.segment,
                    filters.category,
                    filters.productSource,
                  ).map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Finish Type</label>
                <select
                  name="finishType"
                  value={filters.finishType}
                  onChange={handleFilterChange}
                  disabled={!filters.productName}
                >
                  <option value="">All Finishes</option>
                  {getFinishTypes(
                    filters.segment,
                    filters.category,
                    filters.productSource,
                    filters.productName,
                  ).map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Color</label>
                <select
                  name="color"
                  value={filters.color}
                  onChange={handleFilterChange}
                  disabled={
                    !filters.productName ||
                    isRudrakshaProduct(filters.productName)
                  }
                >
                  <option value="">All Colors</option>
                  {getColors(
                    filters.segment,
                    filters.category,
                    filters.productSource,
                    filters.productName,
                    filters.finishType,
                  ).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-field">
                <label>Shape</label>
                <select
                  name="shape"
                  value={filters.shape}
                  onChange={handleFilterChange}
                  disabled={
                    !filters.productName ||
                    isRudrakshaProduct(filters.productName)
                  }
                >
                  <option value="">All Shapes</option>
                  {getShapes(
                    filters.segment,
                    filters.category,
                    filters.productSource,
                    filters.productName,
                  ).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="filters-actions">
              <button onClick={clearFilters} className="btn-secondary">
                Clear Filters
              </button>
            </div>
          </div>

          {/* Table Section */}
          <div className="table-section">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Segment</th>
                    <th>Category</th>
                    <th>Source</th>
                    <th>Product</th>
                    <th>Finish Type</th>
                    <th>Color</th>
                    <th>Shape</th>
                    <th>Carat Rate (Max / Op% / JMI% / Total Max)</th>
                    <th>Ready SLA</th>
                    <th>Order SLA</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSpecs.length > 0 ? (
                    paginatedSpecs.map((product) => {
                      const totals = calculateTotals(product);
                      const isRudra = isRudrakshaProduct(product.productName);

                      return (
                        <React.Fragment key={product.id}>
                          <tr>
                            <td>
                              <strong>{product.segment || "—"}</strong>
                            </td>
                            <td>{product.category || "—"}</td>
                            <td>{product.productSource || "—"}</td>
                            <td>{product.productName || "—"}</td>
                            <td>{product.finishType || "—"}</td>
                            <td>
                              {isRudra ? (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontStyle: "italic",
                                  }}
                                >
                                  N/A
                                </span>
                              ) : (
                                product.color || "—"
                              )}
                            </td>
                            <td>
                              {isRudra ? (
                                <span
                                  style={{
                                    color: "#9ca3af",
                                    fontStyle: "italic",
                                  }}
                                >
                                  N/A
                                </span>
                              ) : (
                                product.shape || "—"
                              )}
                            </td>
                            <td>
                              ₹{totals.maxSeller} / {totals.maxOperator} /{" "}
                              {totals.maxJmi} /{" "}
                              <strong style={{ color: "#15803d" }}>
                                ₹{totals.totalMax}
                              </strong>
                            </td>
                            <td>{fmtVal(product.maxReadyServeSla)}d</td>
                            <td>{fmtVal(product.orderServeSla)}d</td>
                            <td>
                              {editingProduct?.id === product.id ? (
                                <div className="action-buttons">
                                  <button onClick={handleSave} title="Save">
                                    <Save />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingProduct(null);
                                      setError("");
                                    }}
                                    title="Cancel"
                                  >
                                    <X />
                                  </button>
                                </div>
                              ) : (
                                <div className="action-buttons">
                                  <button
                                    onClick={() => handleEdit(product)}
                                    title="Edit"
                                  >
                                    <Edit />
                                  </button>
                                  <button title="View">
                                    <Eye />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>

                          {/* Inline Edit Form */}
                          {editingProduct?.id === product.id && (
                            <tr>
                              <td colSpan="11">
                                <div className="edit-form-container">
                                  <h4>Edit Pearl & Gems Details</h4>
                                  {renderProductForm(
                                    editingProduct,
                                    setEditingProduct,
                                    handleSave,
                                    true,
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="11" className="no-data">
                        No pearl or gems specifications match the selected
                        filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {filteredSpecs.length > 0 && (
            <div className="pagination-controls">
              <div className="pagination-info">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredSpecs.length)} of{" "}
                {filteredSpecs.length} items
              </div>
              <div className="pagination-buttons">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="pagination-page-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="btn-secondary"
                >
                  Next
                </button>
              </div>
              <div className="pagination-options">
                <label>
                  Items per page:
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PearlGemsConstraints;

